import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { tenantApi } from '../api/endpoints/tenant';
import { platformApi } from '../api/endpoints/platform';
import { isPlatformPoolTenant, resolvePlatformContainerTenantId } from '../lib/brandingTenant';

/**
 * Mapa roleId → nombre legible del rol. Une el catálogo de roles del tenant
 * (`tenantApi.listRoles`, campo `roleName`) con el catálogo de roles de plataforma
 * (`platformApi.listPlatformRoles`, campo `displayName`), igual que `MyRoleAssignments`.
 *
 * El admin del pool de plataforma ('*' o 'platform') resuelve con GET /platform/roles; el resto, con
 * /tenants/{tid}/roles (a un usuario de tenant /platform/roles le responde 403).
 *
 * Resolver id→nombre reutilizable: cualquier vista que tenga un roleId crudo (auditoría, asignaciones)
 * lo resuelve a su nombre. Si el catálogo no trae el rol, el caller cae a un fallback legible (el
 * `roleName` que venga en la propia fila, o etiqueta + shortId) — NUNCA al UUID crudo.
 */
export function useRoleNameById(): Map<string, string> {
    const { user } = useAuth();
    const isPlatform = isPlatformPoolTenant(user?.tenantId);
    const tid = resolvePlatformContainerTenantId(user?.tenantId);

    const tenantRolesQ = useQuery({
        queryKey: ['tenant', tid, 'roles'],
        queryFn: () => tenantApi.listRoles(tid ?? ''),
        enabled: !isPlatform && !!tid,
    });
    const platformRolesQ = useQuery({
        queryKey: ['platform', 'roles'],
        queryFn: () => platformApi.listPlatformRoles(),
        enabled: isPlatform,
    });

    const tenantItems = tenantRolesQ.data?.items;
    const platformItems = platformRolesQ.data?.items;

    return useMemo(() => {
        const map = new Map<string, string>();
        // Tenant roles primero; los de plataforma pueden sobreescribir (displayName canónico).
        for (const r of tenantItems ?? []) map.set(r.roleId, r.roleName);
        for (const r of platformItems ?? []) map.set(r.roleId, r.displayName);
        return map;
    }, [tenantItems, platformItems]);
}
